import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Search, ChevronLeft, ChevronRight, Users, ShieldOff } from "lucide-react";
import contactRequests from "../../api/Customer/ContactRequests";

const STORAGE_KEY = "bulk_sms_excluded_contacts";

const ExclusionDrawer = ({ isOpen, onClose, selectedGroups, onExclusionsUpdate }) => {
  const [activeGroupSlug, setActiveGroupSlug] = useState("all");
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 15,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Map of phone_number -> { phone_number, first_name, last_name }
  const [localExclusions, setLocalExclusions] = useState({});

  const debounceRef = useRef(null);

  // Load exclusions from sessionStorage on open
  useEffect(() => {
    if (isOpen) {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const arr = JSON.parse(stored);
          const map = {};
          arr.forEach((item) => { map[item.phone_number] = item; });
          setLocalExclusions(map);
        } catch {
          setLocalExclusions({});
        }
      }
    }
  }, [isOpen]);

  // Reset to first group tab when selected groups change
  useEffect(() => {
    if (isOpen && selectedGroups.length > 0) {
      setActiveGroupSlug("all");
      setPagination((p) => ({ ...p, currentPage: 1 }));
    }
  }, [isOpen, selectedGroups]);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPagination((p) => ({ ...p, currentPage: 1 }));
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    if (!isOpen || selectedGroups.length === 0) return;
    setIsLoading(true);
    try {
      const slug =
        activeGroupSlug === "all"
          ? selectedGroups.map((g) => g.contact_group_slug).join(",")
          : activeGroupSlug;
      const res = await contactRequests.displayGroupContacts(
        slug,
        pagination.currentPage,
        pagination.perPage,
        debouncedSearch
      );
      if (res.success) {
        const payload = res.data?.data?.data;
        const list = payload?.data || [];
        const pg = payload?.pagination || {};
        setContacts(list);
        setPagination((prev) => ({
          ...prev,
          total: pg.total ?? 0,
          lastPage: pg.last_page ?? 1,
          from: pg.from ?? 0,
          to: pg.to ?? 0,
          currentPage: pg.current_page ?? prev.currentPage,
        }));
      } else {
        setContacts([]);
      }
    } catch {
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, activeGroupSlug, selectedGroups, pagination.currentPage, pagination.perPage, debouncedSearch]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const persistExclusions = (updated) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.values(updated)));
    onExclusionsUpdate(Object.values(updated));
  };

  const toggleExclusion = (contact) => {
    setLocalExclusions((prev) => {
      const updated = { ...prev };
      if (updated[contact.phone_number]) {
        delete updated[contact.phone_number];
      } else {
        updated[contact.phone_number] = {
          phone_number: contact.phone_number,
          first_name: contact.first_name || "",
          last_name: contact.last_name || "",
        };
      }
      persistExclusions(updated);
      return updated;
    });
  };

  const handleGroupTab = (slug) => {
    setActiveGroupSlug(slug);
    setSearch("");
    setDebouncedSearch("");
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, currentPage: page }));
  };

  const exclusionCount = Object.keys(localExclusions).length;

  const getPageNumbers = () => {
    const { currentPage, lastPage } = pagination;
    if (lastPage <= 7) return Array.from({ length: lastPage }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", lastPage];
    if (currentPage >= lastPage - 3) return [1, "...", lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1, lastPage];
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", lastPage];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 z-50 flex flex-col h-full bg-white shadow-2xl w-full max-w-lg"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg border border-red-100">
                  <ShieldOff className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-800">Exclude Numbers</h2>
                  <p className="text-xs text-gray-500">Select contacts to exclude from this campaign</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Group Tabs */}
            {selectedGroups.length > 1 && (
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <p className="text-xs font-medium text-gray-500 mb-2">Browse by group</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleGroupTab("all")}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      activeGroupSlug === "all"
                        ? "bg-red-600 text-white"
                        : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    All Groups
                  </button>
                  {selectedGroups.map((g) => (
                    <button
                      key={g.contact_group_slug}
                      onClick={() => handleGroupTab(g.contact_group_slug)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        activeGroupSlug === g.contact_group_slug
                          ? "bg-red-600 text-white"
                          : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {g.group_name || g.contact_group_name || "Group"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search */}
            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or phone number..."
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Contact List */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-48 gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-red-600 animate-spin border-t-transparent" />
                  <p className="text-sm text-gray-500">Loading contacts...</p>
                </div>
              ) : contacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
                  <Users className="w-10 h-10" />
                  <p className="text-sm">No contacts found</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {contacts.map((contact) => {
                    const isExcluded = !!localExclusions[contact.phone_number];
                    const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—";
                    return (
                      <li
                        key={`${contact.id}-${contact.phone_number}`}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                          isExcluded ? "bg-red-50/50" : ""
                        }`}
                        onClick={() => toggleExclusion(contact)}
                      >
                        <input
                          type="checkbox"
                          checked={isExcluded}
                          onChange={() => toggleExclusion(contact)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded accent-red-600 cursor-pointer flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{fullName}</p>
                          <p className="text-xs text-gray-500">{contact.phone_number}</p>
                        </div>
                        {isExcluded && (
                          <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-100 flex-shrink-0">
                            Excluded
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Pagination */}
            {!isLoading && pagination.total > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">
                    Showing {pagination.from}–{pagination.to} of {pagination.total}
                  </p>
                  <select
                    value={pagination.perPage}
                    onChange={(e) =>
                      setPagination((p) => ({ ...p, perPage: Number(e.target.value), currentPage: 1 }))
                    }
                    className="text-xs border border-gray-200 rounded px-2 py-1 bg-white text-gray-700"
                  >
                    {[10, 15, 25, 50].map((n) => (
                      <option key={n} value={n}>{n} / page</option>
                    ))}
                  </select>
                </div>
                {pagination.lastPage > 1 && (
                  <div className="flex items-center gap-1 justify-center flex-wrap">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    {getPageNumbers().map((p, idx) =>
                      p === "..." ? (
                        <span key={`ellipsis-${idx}`} className="px-1 text-gray-400 text-xs">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p)}
                          className={`min-w-[28px] h-7 text-xs rounded-lg border transition-colors ${
                            p === pagination.currentPage
                              ? "bg-red-600 text-white border-red-600"
                              : "border-gray-200 text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.lastPage}
                      className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="px-4 py-4 border-t border-gray-200 bg-white flex-shrink-0">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-red-600">{exclusionCount}</span>{" "}
                  {exclusionCount === 1 ? "contact" : "contacts"} selected for exclusion
                </p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Apply & Close
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExclusionDrawer;
